import {gql} from '@apollo/client';
import React, {useRef} from 'react';
import _ from 'underscore';

import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {assert, assertAndContinue} from 'Asserts';
import {LabelTemplate, Printer} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import * as Context from 'Context';
import {LabelPrintingMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  BenchlingQueryResult,
  QueryResultType,
  SkippableBenchlingQueryResult,
  useBenchlingQuery,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import SampleStatusTable from 'inventory/sample-status/SampleStatusTable';
import {checkForSampleStatusWarnings} from 'inventory/sample-status/SampleStatusWarning';
import BartenderPrintModal from 'label-printing/BartenderPrintModal';
import {buildPrintableItems, LabelPrintable, NotPrintableError} from 'label-printing/LabelPrintableSummary';
import * as LabelPrintingActions from 'label-printing/LabelPrintingActions';
import LabelTemplateSelector from 'label-printing/LabelTemplateSelector';
import PrinterSelector from 'label-printing/PrinterSelector';
import PrintLabelTimezonePicker from 'label-printing/PrintLabelTimezonePicker';
import {ZebraDevice} from 'label-printing/ZebraBrowserPrint';
import ZebraBrowserPrinterSelector from 'label-printing/ZebraBrowserPrinterSelector';
import {printZebraLabels} from 'label-printing/ZebraLabelPrintingActions';
import navigateToURL from 'lib/navigateToURL';
import {PrintPreferences} from 'notebook/mediocre/print/PrintConstants';
import notifications from 'notifications';
import preferences from 'Preferences';
import * as RegistryUtil from 'registry/RegistryUtil';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';
import {NumericInputChangeEvent} from 'util/Types';

import {
  PrintBarcodeModalLabelParamsQuery,
  PrintBarcodeModalLabelParamsQueryVariables,
} from './__generated/PrintBarcodeModalLabelParamsQuery';
import {PrintBarcodeModalQuery, PrintBarcodeModalQueryVariables} from './__generated/PrintBarcodeModalQuery';

type RegistryGQL = PrintBarcodeModalQuery['registries'][number];
type LabelPrintingConfig = PrintBarcodeModalQuery['labelPrintingConfig'];

export interface PrintBarcodeModalProps {
  onRequestClose: () => void;
  itemIds: Array<string>;
  analyticsMetadata?: AnalyticsMetadataForContext;
  shouldAlwaysShowSampleStatusTable?: boolean;
}

interface EnhancedProps {
  currentUserId: string;
  isRegistryWritable: boolean;
  labelPrintingConfig: LabelPrintingConfig;
  printableItems: Array<LabelPrintable>;
  registry: RegistryGQL;
  tenantId: string;
}

interface State {
  labelTemplateId: string | null;
  labelTemplateName: string | null;
  printerId: string | null;
  zebraPrinter: ZebraDevice | null;
  copies: number | null;
  selectedItemIds: Array<string>;
  samples: Array<SampleStatusItem> | null;
}

function sharedRegistryId(items: Array<LabelPrintable>): string {
  const registryIds = new Set(items.map((s) => s.schemaRegistryId));
  assertAndContinue(
    registryIds.size === 1,
    `Got ${registryIds.size} distinct registry IDs, expected exactly one.`
  );
  return Array.from(registryIds)[0];
}

function renderModal(
  contents: JSX.Element,
  isSubmitDisabled: boolean,
  onRequestClose: () => void,
  onRequestSubmit: () => void
): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          disabled: isSubmitDisabled,
          submit: true,
          style: ButtonStyle.PRIMARY,
          text: 'Print',
        },
      ]}
      enableRedesign={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      title="Print labels"
    >
      {contents}
    </Modal>
  );
}

export const PRINT_BARCODE_MODAL_QUERY = gql`
  query PrintBarcodeModalQuery($bioentityIds: [StrictString!]!, $storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    currentTenant {
      id
    }
    currentUser {
      ... on UserNode {
        id
      }
      ... on ServiceAccountActorNode {
        id
      }
      ... on BenchlingAppNode {
        id
      }
    }
    labelPrintingConfig {
      id
      userConfiguredPrintingMethod
      bartenderServers {
        id
        url
        name
      }
    }
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        ...BioentitySummaryGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    storables(ids: $storableIds) {
      ... on StorableInterface {
        ...StorableSummaryGraphQL
        ... on ContainerNode {
          id
          contents {
            id
            containedItem {
              ... on FolderItemInterface {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    registries {
      id
      ownerJson
      permissions {
        readable
        writable
      }
    }
  }
  ${getFragmentWrapper('BioentitySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('StorableSummary').fragment}
`;

// `labelParamsJson` is expensive to resolve and is only consumed by the BarTender print flow, so it is
// fetched in this separate query that is skipped unless the tenant prints via BarTender.
export const PRINT_BARCODE_MODAL_LABEL_PARAMS_QUERY = gql`
  query PrintBarcodeModalLabelParamsQuery($bioentityIds: [StrictString!]!, $storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        id
        labelParamsJson
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    storables(ids: $storableIds) {
      ... on StorableInterface {
        id
        labelParamsJson
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function renderQueryModal(
  props: PrintBarcodeModalProps,
  result: BenchlingQueryResult<PrintBarcodeModalQuery, PrintBarcodeModalQueryVariables>,
  labelParamsResult: SkippableBenchlingQueryResult<
    PrintBarcodeModalLabelParamsQuery,
    PrintBarcodeModalLabelParamsQueryVariables
  >
): JSX.Element | null {
  if (result.type === QueryResultType.ERROR || labelParamsResult.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING || labelParamsResult.type === QueryResultType.PENDING) {
    return renderModal(<CenteredHorizontalThrobber />, true, props.onRequestClose, () => {});
  } else {
    const printableItems: Array<LabelPrintable> = buildPrintableItems(
      result.data.bioentities,
      result.data.storables,
      labelParamsResult
    );
    const registryId = sharedRegistryId(printableItems);
    const registry = result.data.registries.find(({id}) => id === registryId) || null;
    if (!registry) {
      throw new NotPrintableError(`Registry for items to print should be accessible`);
    }
    const isRegistryWritable = registry.permissions.writable;
    return (
      <PrintBarcodeModal
        {...props}
        labelPrintingConfig={result.data.labelPrintingConfig}
        printableItems={printableItems}
        registry={registry}
        isRegistryWritable={isRegistryWritable}
        currentUserId={result.data.currentUser!.id}
        tenantId={result.data.currentTenant.id}
      />
    );
  }
}

function QueryingPrintBarcodeModal(props: PrintBarcodeModalProps): JSX.Element | null {
  const itemIds = [...new Set(props.itemIds)];
  const bioentityIds = itemIds.filter((id) => APIUtil.isBiologicalEntity(id));
  const storableIds = itemIds.filter((id) => APIUtil.isStorable(id));

  const result = useBenchlingQuery<PrintBarcodeModalQuery, PrintBarcodeModalQueryVariables>(
    PRINT_BARCODE_MODAL_QUERY,
    {
      variables: {bioentityIds, storableIds},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  // Only the BarTender print flow reads `labelParamsJson`, and resolving it is expensive, so we fetch it in a
  // second query that is skipped for every other printing method.
  const isBartender =
    result.type === QueryResultType.SUCCESS &&
    result.data.labelPrintingConfig.userConfiguredPrintingMethod === LabelPrintingMethod.BARTENDER;

  const labelParamsResult = useSkippableBenchlingQuery<
    PrintBarcodeModalLabelParamsQuery,
    PrintBarcodeModalLabelParamsQueryVariables
  >(PRINT_BARCODE_MODAL_LABEL_PARAMS_QUERY, {
    variables: {bioentityIds, storableIds},
    skip: !isBartender,
    fetchPolicy: 'cache-and-network', // fetch latest `labelParamsJson` so updated info can be printed
    renderCachedDataWhenRefetching: true,
  });

  let component: JSX.Element | null = null;
  const errorToastShown = useRef<boolean>(false);
  try {
    component = renderQueryModal({...props, itemIds}, result, labelParamsResult);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    component = null;
    if (!errorToastShown.current) {
      notifications.error(`Error while displaying labels to print: ${e.message}.`);
      errorToastShown.current = true;
    }
  }
  return component;
}

export class PrintBarcodeModal extends React.Component<PrintBarcodeModalProps & EnhancedProps, State> {
  constructor(props: PrintBarcodeModalProps & EnhancedProps) {
    super(props);
    let labelTemplateId;
    let printerId;
    const prefs = preferences.get(
      `${PrintPreferences.LABEL_PRINTING_OPTIONS_NAMESPACE}.${this.props.printableItems[0].schemaId}`
    );

    if (
      this.props.shouldAlwaysShowSampleStatusTable &&
      props.printableItems.some(({id}) => !APIUtil.isStorable(id))
    ) {
      // TODO(BNCH-76231): remove once SampleStatusTable supports displaying non-storables
      assert(false, 'PrintBarcodeModal cannot display SampleStatusTable with non-storables');
    }

    if (prefs) {
      labelTemplateId = prefs.labelTemplateId;
      printerId = prefs.printerId;
    } else {
      labelTemplateId = null;
      printerId = null;
    }
    this.state = {
      labelTemplateId,
      labelTemplateName: null,
      printerId,
      zebraPrinter: null,
      copies: 1,
      selectedItemIds: this.props.printableItems.map(({id}) => id),
      samples: null,
    };
  }

  override async componentDidMount(): Promise<void> {
    if (this.props.shouldAlwaysShowSampleStatusTable) {
      const {samples} = await checkForSampleStatusWarnings({
        sampleIds: this.props.printableItems.map(({id}) => id),
        shouldAlwaysShowWarning: true,
      });
      this.setState({samples: samples ?? null});
    }
  }

  private handleCreateLabelTemplate = (): void => {
    navigateToURL(
      RegistryUtil.getCreateLabelTemplateUrl(
        this.props.registry.ownerJson.url_identifier,
        this.props.registry.id
      )
    );
    this.props.onRequestClose();
  };

  private handleChangeLabelTemplate = (labelTemplate: LabelTemplate | null): void => {
    this.setState({
      labelTemplateId: labelTemplate ? labelTemplate.id : null,
      labelTemplateName: labelTemplate ? labelTemplate.name : null,
    });
  };

  private handleCreatePrinter = (): void => {
    navigateToURL(
      RegistryUtil.getCreatePrinterUrl(this.props.registry.ownerJson.url_identifier, this.props.registry.id)
    );
    this.props.onRequestClose();
  };

  private handleChangePrinter = (printer: Printer | null): void => {
    this.setState({printerId: printer ? printer.id : null});
  };

  private handleChangeZebraPrinter = (zebraPrinter: ZebraDevice | null): void => {
    this.setState({zebraPrinter});
  };

  private setSelectedItemIds = (ids: Array<string>): void => {
    this.setState({selectedItemIds: ids});
  };

  private handleSubmit = async (): Promise<void> => {
    const labelTemplateId = this.state.labelTemplateId;
    const labelTemplateName = this.state.labelTemplateName;
    const printerId = this.state.printerId;
    const itemsToPrint = this.props.printableItems.filter(({id}) => this.state.selectedItemIds.includes(id));
    preferences.set(
      `${PrintPreferences.LABEL_PRINTING_OPTIONS_NAMESPACE}.${this.props.printableItems[0].schemaId}`,
      {
        labelTemplateId,
        printerId,
      }
    );
    if (
      this.props.labelPrintingConfig.userConfiguredPrintingMethod === LabelPrintingMethod.ZEBRA_BROWSER_PRINT
    ) {
      await printZebraLabels({
        printableItems: itemsToPrint,
        labelTemplateId: labelTemplateId!,
        labelTemplateName: labelTemplateName!,
        device: this.state.zebraPrinter!,
        copies: this.state.copies!,
        currentUserId: this.props.currentUserId,
        tenantId: this.props.tenantId,
        analyticsMetadata: this.props.analyticsMetadata ?? {},
      });
    } else {
      // To preserve historical non-blocking behavior, we don't use await Context.usingAsync().
      // This is safe because the Context.get() consumer (printLabels task) is called synchronously.
      Context.using({analyticsMetadata: this.props.analyticsMetadata}, null, () => {
        LabelPrintingActions.printLabels(
          // eslint-disable-next-line no-restricted-syntax
          _.pluck(itemsToPrint, 'id'),
          labelTemplateId!,
          printerId!,
          this.state.copies!
        );
      });
    }
    this.props.onRequestClose();
  };

  renderBarcodeDisplay(barcodes: Array<string>): JSX.Element {
    return (
      <>
        {this.props.shouldAlwaysShowSampleStatusTable && this.state.samples ? (
          <SampleStatusTable
            samples={this.state.samples}
            allowSelection={true}
            selectedSampleIds={this.state.selectedItemIds}
            setSelectedSampleIds={this.setSelectedItemIds}
            canSelectMultiple={true}
          />
        ) : (
          <>
            <FieldLabel label="Barcodes:" />
            <span className="u-small-spacing-left">{barcodes.join(', ')}</span>
          </>
        )}
      </>
    );
  }

  renderModalContents(): JSX.Element {
    const barcodes = this.props.printableItems.map((item) => item.barcodeOrName);
    return (
      <div>
        {this.renderBarcodeDisplay(barcodes)}
        <FormGroup>
          <Row>
            <Col md={12}>
              <FieldLabel className="u-small-spacing-top" isRequired={true} label="Label Template" />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <LabelTemplateSelector
                onChange={this.handleChangeLabelTemplate}
                labelTemplateId={this.state.labelTemplateId}
                registryId={sharedRegistryId(this.props.printableItems)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              {this.props.isRegistryWritable && (
                <div>
                  Don't see the label template you're looking for?&nbsp;
                  <a onClick={this.handleCreateLabelTemplate}>Create a new one</a>.
                </div>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FieldLabel className="u-small-spacing-top" isRequired={true} label="Printer" />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              {this.props.labelPrintingConfig.userConfiguredPrintingMethod ===
              LabelPrintingMethod.ZEBRA_BROWSER_PRINT ? (
                <ZebraBrowserPrinterSelector
                  device={this.state.zebraPrinter}
                  onChange={this.handleChangeZebraPrinter}
                />
              ) : (
                <PrinterSelector
                  onChange={this.handleChangePrinter}
                  printerId={this.state.printerId}
                  registryId={sharedRegistryId(this.props.printableItems)}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              {this.props.isRegistryWritable &&
                this.props.labelPrintingConfig.userConfiguredPrintingMethod !==
                  LabelPrintingMethod.ZEBRA_BROWSER_PRINT && (
                  <div>
                    Don't see the printer you're looking for?&nbsp;
                    <a onClick={this.handleCreatePrinter}>Create a new one</a>.
                  </div>
                )}
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FieldLabel
                className="u-small-spacing-top"
                isRequired={true}
                label="Number of copies per label"
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <NumericInput
                min={1}
                required={true}
                onChange={(e: NumericInputChangeEvent) => {
                  this.setState({copies: e.target.numericValue});
                }}
                value={this.state.copies}
                validateInteger={true}
              />
            </Col>
          </Row>
          <PrintLabelTimezonePicker />
        </FormGroup>
      </div>
    );
  }

  isSubmitDisabled(): boolean {
    const printerSelected =
      this.props.labelPrintingConfig.userConfiguredPrintingMethod === LabelPrintingMethod.ZEBRA_BROWSER_PRINT
        ? this.state.zebraPrinter
        : this.state.printerId;
    return (
      !this.state.selectedItemIds.length ||
      this.state.copies == null ||
      !this.state.labelTemplateId ||
      !printerSelected
    );
  }

  override render(): JSX.Element {
    if (this.props.labelPrintingConfig.userConfiguredPrintingMethod === LabelPrintingMethod.BARTENDER) {
      return (
        <BartenderPrintModal
          userId={this.props.currentUserId}
          tenantId={this.props.tenantId}
          labelPrintingConfig={this.props.labelPrintingConfig}
          printableItems={this.props.printableItems}
          onRequestClose={this.props.onRequestClose}
          analyticsMetadata={this.props.analyticsMetadata}
          shouldAlwaysShowSampleStatusTable={this.props.shouldAlwaysShowSampleStatusTable}
          samples={this.state.samples}
        />
      );
    } else {
      return renderModal(
        this.renderModalContents(),
        this.isSubmitDisabled(),
        this.props.onRequestClose,
        this.handleSubmit
      );
    }
  }
}

export default QueryingPrintBarcodeModal;
